import { Inject, Injectable } from '@angular/core';
import io from 'socket.io-client';
import { LogLevel } from 'src/app/common/contracts/logging/log-level';

import makeDebug from '../../../makeDebug';
import { ConnectionStateService } from './connection-state.service';
import { IFeathersService } from './contracts/sync/feathers-service';
import { IWebSocketClient } from './contracts/sync/web-socket-client';
import { EnvironmentService } from './environment/environment.service';
import { FeathersService } from './feathers.service';

const debug = makeDebug('websocket:websocket-client');

/**
 * Opens the socket.io connection to the backend and reports whether it is up.
 *
 * `connect()` is called once per process by the `ServerConnector` element of the start pipeline.
 * It creates the socket, hands it to the `FeathersService` (every Feathers service call travels
 * over this connection) and resolves as soon as the first attempt has either connected or
 * failed, so the start pipeline continues offline when the backend is unreachable.
 *
 * Reconnects are socket.io's job: after a lost transport it retries forever with its default
 * backoff (1 s, doubling with jitter, capped at 5 s). This class never reconnects itself. The
 * event handlers are registered once and fire on every cycle: `disconnect` and `connect_error`
 * report `disconnected`, `connect` reports `connected` — a fresh connection carries no
 * authentication, so somebody has to authenticate it again after every reconnect.
 *
 * That somebody is not this class. Its only output is the `socket` field of
 * `ConnectionStateService`, one `setSocket()` call per socket event. The `SessionController`
 * re-authenticates the connection on every `connected` and takes the session offline on
 * `disconnected`; everything else reads the connection state.
 */
@Injectable({ providedIn: 'root' })
export class WebSocketClient implements IWebSocketClient {
  private _socket: SocketIOClient.Socket;

  constructor(
    @Inject(FeathersService) private _feathersService: IFeathersService,
    private _connectionState: ConnectionStateService,
    private _environmentService: EnvironmentService
  ) {}

  /**
   * Resolves once the first connection attempt has either connected or failed, so the pipeline can
   * continue either way. socket.io keeps reconnecting on its own afterwards; every later change is
   * reported through the connection state.
   */
  public async connect(uri: string): Promise<void> {
    await this.openSocket(uri);
    this._feathersService.setup(this._socket);
  }

  private openSocket(uri: string): Promise<void> {
    return new Promise<void>(resolve => {
      this.disposeSocket();

      this._socket = io.connect(uri, {
        transports: ['websocket'],
        upgrade: false,
        forceNew: true,
        query: {
          albertaVersion: this._environmentService.version,
          albertaBranch: this._environmentService.branch,
        },
      });

      this._socket.on('connect', () => {
        this._connectionState.setSocket('connected');
        resolve();
      });

      this._socket.on('connect_error', (error: unknown) => {
        window.logger.error('connection could not be established', toError(error), LogLevel.silent);
        this._connectionState.setSocket('disconnected', 'connect_error');
        resolve();
      });

      this._socket.on('error', (error: unknown) => {
        window.logger.error('connection could not be established', toError(error), LogLevel.silent);
        // an error packet does not necessarily end the connection: follow what the socket says
        if (!this._socket.connected) {
          this._connectionState.setSocket('disconnected', 'error');
        }
        resolve();
      });

      this._socket.on('disconnect', (reason: string) => {
        window.logger.error(`disconnect: ${reason}`, new Error(`socket disconnected: ${reason}`), LogLevel.silent);
        this._connectionState.setSocket('disconnected', `disconnect: ${reason}`);
      });
    });
  }

  private disposeSocket(): void {
    if (!this._socket) {
      return;
    }
    this._socket.disconnect();
    this._socket = null;
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}
